import { clipPlaybackRate } from './clipProperties';
import { recalcClipStarts, safeClipDuration } from './editorClips';
import { snapTimeToFrame } from './frameGrid';
import type { EditorTimelineClip } from '@/types/editor';

export type TimelineInsertMode = 'append' | 'insert' | 'overwrite';

export interface TimelineClipEditResult {
  clips: EditorTimelineClip[];
  insertAt: number;
  rippleDelta: number;
}

const MIN_CLIP_DURATION = 0.5;

function totalDuration(clips: EditorTimelineClip[]) {
  return clips.reduce((sum, clip) => sum + safeClipDuration(clip.duration), 0);
}

export function addClipByTimelineMode(
  clips: EditorTimelineClip[],
  incoming: EditorTimelineClip,
  playhead: number,
  mode: TimelineInsertMode,
  makeId: (prefix: string) => string
): TimelineClipEditResult {
  const source = recalcClipStarts(clips);
  const total = totalDuration(source);
  if (mode === 'append' || source.length === 0) {
    return {
      clips: recalcClipStarts([...source, { ...incoming, start: total }]),
      insertAt: total,
      rippleDelta: 0,
    };
  }

  const at = snapTimeToFrame(Math.min(total, Math.max(0, playhead)));
  const incomingDuration = safeClipDuration(incoming.duration);
  if (mode === 'insert') {
    const next: EditorTimelineClip[] = [];
    let inserted = false;
    for (const clip of source) {
      const start = clip.start;
      const end = start + clip.duration;
      if (!inserted && at <= start + 1e-6) {
        next.push({ ...incoming, start: at, transitionIn: undefined });
        inserted = true;
      }
      if (!inserted && at > start && at < end) {
        const leftDuration = snapTimeToFrame(at - start);
        const rightDuration = snapTimeToFrame(end - at);
        if (leftDuration >= MIN_CLIP_DURATION) next.push({ ...clip, duration: leftDuration });
        next.push({ ...incoming, start: at, transitionIn: undefined });
        inserted = true;
        if (rightDuration >= MIN_CLIP_DURATION) {
          next.push({
            ...clip,
            id: makeId('clip'),
            mediaOffset: clip.mediaOffset + leftDuration * clipPlaybackRate(clip),
            duration: rightDuration,
            transitionIn: undefined,
            isTrimmed: true,
          });
        }
        continue;
      }
      next.push(clip);
    }
    if (!inserted) next.push({ ...incoming, start: total, transitionIn: undefined });
    return { clips: recalcClipStarts(next), insertAt: at, rippleDelta: incomingDuration };
  }

  const overwriteEnd = at + incomingDuration;
  const before: EditorTimelineClip[] = [];
  const after: EditorTimelineClip[] = [];
  for (const clip of source) {
    const start = clip.start;
    const end = start + clip.duration;
    if (end <= at + 1e-6) {
      before.push(clip);
      continue;
    }
    if (start >= overwriteEnd - 1e-6) {
      after.push(clip);
      continue;
    }
    if (start < at) {
      const prefixDuration = snapTimeToFrame(at - start);
      if (prefixDuration >= MIN_CLIP_DURATION) before.push({ ...clip, duration: prefixDuration });
    }
    if (end > overwriteEnd) {
      const consumed = snapTimeToFrame(overwriteEnd - start);
      const suffixDuration = snapTimeToFrame(end - overwriteEnd);
      if (suffixDuration >= MIN_CLIP_DURATION) {
        after.unshift({
          ...clip,
          id: makeId('clip'),
          mediaOffset: clip.mediaOffset + consumed * clipPlaybackRate(clip),
          duration: suffixDuration,
          transitionIn: undefined,
          isTrimmed: true,
        });
      }
    }
  }
  return {
    clips: recalcClipStarts([...before, { ...incoming, start: at, transitionIn: undefined }, ...after]),
    insertAt: at,
    rippleDelta: 0,
  };
}

